import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, fmtCurrency, fmtDate } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/fintech/StatusPill';
import { toast } from 'sonner';

export default function WithdrawPage() {
  const { t } = useLanguage();
  const { user, refresh } = useAuth();
  const [sp] = useSearchParams();
  const [currency, setCurrency] = useState(sp.get('currency') || 'USD');
  const [amount, setAmount] = useState('');
  const [form, setForm] = useState({ recipient_name: '', recipient_account: '', recipient_bank: '', swift_bic: '', notes: '' });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/wallet/withdrawals').then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const canSubmit = user?.kyc_status === 'APPROVED';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/wallet/withdrawals', { currency, amount: Number(amount), method: 'bank_transfer', ...form });
      toast.success('Withdrawal request submitted');
      setAmount(''); setForm({ recipient_name: '', recipient_account: '', recipient_bank: '', swift_bic: '', notes: '' });
      await load(); await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('wallet.withdraw')}</h1>
      {!canSubmit && <div className="rounded-2xl border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/10 p-4 text-sm">{t('wallet.kyc_required')}</div>}

      <Card className="bg-white"><CardContent className="p-6">
        <form onSubmit={submit} className="space-y-4" data-testid="withdraw-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('common.currency')}</Label><Select value={currency} onValueChange={setCurrency}><SelectTrigger data-testid="withdraw-currency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="KMF">KMF</SelectItem><SelectItem value="KZT">KZT</SelectItem></SelectContent></Select></div>
            <div><Label>{t('wallet.amount')}</Label><Input required type="number" step="0.01" min="0.01" data-testid="withdraw-amount" value={amount} onChange={e => setAmount(e.target.value)} className="tabular-nums" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('wallet.recipient_name')}</Label><Input required data-testid="withdraw-recipient-name" value={form.recipient_name} onChange={e => setForm({ ...form, recipient_name: e.target.value })} /></div>
            <div><Label>{t('wallet.recipient_account')}</Label><Input required data-testid="withdraw-recipient-account" value={form.recipient_account} onChange={e => setForm({ ...form, recipient_account: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('wallet.recipient_bank')}</Label><Input required value={form.recipient_bank} onChange={e => setForm({ ...form, recipient_bank: e.target.value })} /></div>
            <div><Label>{t('wallet.swift_bic')}</Label><Input value={form.swift_bic} onChange={e => setForm({ ...form, swift_bic: e.target.value })} /></div>
          </div>
          <div><Label>{t('wallet.notes')}</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <Button type="submit" disabled={busy || !canSubmit} data-testid="withdraw-submit" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">{t('wallet.submit_withdraw')}</Button>
        </form>
      </CardContent></Card>

      <Card className="bg-white overflow-hidden">
        <div className="p-4 border-b font-serif text-lg">{t('wallet.pending_requests')}</div>
        {items.length === 0 ? <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.empty')}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Currency</th><th className="p-3 text-right">Amount</th><th className="p-3 text-left">Recipient</th><th className="p-3 text-left">Status</th></tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t">
                  <td className="p-3 text-xs">{fmtDate(it.created_at)}</td>
                  <td className="p-3">{it.currency}</td>
                  <td className="p-3 text-right tabular-nums">{fmtCurrency(it.amount, it.currency)}</td>
                  <td className="p-3 text-xs">{it.details?.recipient_name}<div className="text-[hsl(var(--muted-foreground))]">{it.details?.recipient_account}</div></td>
                  <td className="p-3"><StatusPill status={it.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
